import { CSATSurveyData } from 'components/CSAT/types'
import { CustomerModel } from 'pages/Customers/helpers/types'
import { AccountUserGroup } from './accounts'
import {
  Address,
  BeneficiaryDetails,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
} from './beneficiaries'
import { Channel } from './channels'
import { Contact, ContactInstrument, InstrumentType } from './contacts'
import { CustomerWallet, CustomerWalletDepositDetails } from './customers'
import { WalletTransferType } from './unifiedPayouts/unifiedPayouts'
import { WalletStatus } from './wallets'

export const Partner = { JNFX: 'JnFx', BBVA: 'BBVA' } as const
export type Partner = (typeof Partner)[keyof typeof Partner]

export interface WalletType {
  approxConvertedAvailable: string
  approxBalance: string
  address: string | null
  alternatives: Alternative[]
  available: number
  balance: number
  currency: Currency
  depositFee: number
  description: string
  id: number
  lookup: null
  protocol: string
  supportsDeposits: boolean
  supportsWithdrawals: boolean
  supportsThirdParty: boolean
  supportsInternalTransfers?: boolean
  withdrawalFee: number
  isEmoney?: boolean
  partner?: Partner
  lsid?: string
  status?: WalletStatus
  supportedTransferDestinations: BeneficiaryTransferDestination[]
  supportsInternalBvnkNetworkTransfers?: boolean
  convertedAvailable?: number
  tags?: string[]
}

interface Alternative {
  protocol: string
  address: string
  tag?: string
  uri: string
  amount?: number
}

export interface CurrencyBase {
  code: string
  name: string
  fiat: boolean
}

export interface Currency extends CurrencyBase {
  depositFee: number
  icon: string
  id: number
  options: Options
  pricePrecision: number
  protocols: Protocol[]
  quantityPrecision: number
  supportsDeposits: boolean
  supportsWithdrawals: boolean
  withdrawalFee: number
  withdrawalParameters: WithdrawalParameters[]
}

export interface WithdrawalParameters {
  code: string
  example: string
  id: number
  name: string
  required: boolean
}

export type Option = {
  label: string
  value: string
}

export interface Options {
  transaction: string
  address: string
  explorer: string
  confirmations: number
}

export interface Paging {
  offset: number
  total?: number
  max: number
}

export interface Protocol {
  code: string
  network: string
  networkCode: string
}

export interface FormFieldsMerchant {
  merchantName: string
  walletId: string
  webhookUrl: string
}

export interface MerchantType {
  id: number
  merchantId: string
  displayName: string
  secret: string
  webhookUrl: string | null
  autoConvertInvalidPayments: boolean
  defaultExpiryMinutes: number
  webhookVersion: number
  wallet: WalletType
  emailRecipients: null
}

export interface AddMerchantPayload {
  displayName: string
  webhookUrl?: string
  wallet: {
    id: number
  }
}

export interface EditMerchantPayload {
  displayName: string
  webhookUrl?: string
  wallet: number
  merchant: MerchantType
}

export type MerchantInfo = {
  merchantId: string
  displayName: string
  wallet: {
    currency: {
      code: string
    }
    id: number
  }
}
export interface ErrorAPI {
  errorList: Error[]
}

export interface ErrorData {
  error: string
  message: string
  status: number
}

export interface Error {
  parameter: string
  code: string
  message: string
}

export interface ErrorResponseV2 {
  code: string
  documentLink: string
  message: string
  status: string
}

export interface FormFieldsAccountUser {
  emailAddress: string
  groups: AccountUserGroup[]
}

export interface SelectOption {
  value: string
  label: string
}

export interface WorkspaceListItem {
  value: string
  label: string
}

export interface WorkspaceListItemId {
  id: string
  name: string
}

export interface FilterListItems {
  order: number
  value: string
  name: string
}

export interface IDateValue {
  startDate: string | null | Date
  endDate: string | null | Date
}

export type LegalAgreementsCallout = { text: string }

export interface GenericFeatureFlags {
  [key: string]: unknown
}

export type EnableMaltaAccounts = {
  disallowPaymentCurrencies?: string[]
}

export type NamedFeatureFlags = {
  enableAddEmbeddedIndividual: boolean
  'show-wallet-total': boolean
  enableBanner: EnableBanner
  enableLegalAgreements: boolean
  enableLegalAgreementsCallout: LegalAgreementsCallout
  enableTwoFaAuth: boolean
  enableRestrictDepositCurrencyToCrypto: boolean
  enablePaymentsCsvExport: boolean
  enableCasePortalCustomerId: boolean
  enableCasePortalSupportTableUpdate: boolean
  enableCsatSurveyCasePortal: boolean
  enableOptionalEpcFields: boolean
  enablePayrollRole: boolean
  enableManageMerchants: boolean
  enableUsLaunchRestrictions: boolean
  enableUsRestrictedCurrencies: string[]
  enableCustomerCryptoWalletCreation: boolean
  enableCustomerFee: boolean
  enableCustomerFeeSetup: boolean
  enableDarkMode: boolean
  enablePayoutPaymentMethodSelection: boolean
  enableEpcDashboard: boolean
  enableCustomerTimelineTab: boolean
  enableTransactionStore: boolean
  enableRelianceBusinessFields: boolean
  enableMaltaAccounts: EnableMaltaAccounts
  enableWalletSorting: boolean
  enableLegacyBeneficiaryCreation: boolean
  enableEpcV2CustomerCreation: boolean
  enableEpcV2RelianceCustomerCreation: boolean
  enableEpcWalletConversion: boolean
  enableV3Convert: boolean
  enableV3Contacts: boolean
  enableWalletCreationV2: boolean
  enableBeneficiaryPayouts: boolean
  enableBilling: boolean
  enablePaymentLimitsManagement: boolean
  enableCards: boolean
  enableWalletVerification: boolean
  enableEpWalletCurrencies: EnableEPWalletCurrencies
  enableHybridAddress: boolean
  enableCryptoFiatSend: boolean
  enableContactsMFE: boolean
  enableAddAccountSheet: boolean
  enableConsolidatedPayments: boolean
  enableIndentificationCodeType: boolean
  enableEarn: boolean
  enableHppSettingsPage: boolean
  enableAddEmbeddedV2Individual: boolean
  enableGovernmentIds: boolean
}

export type FeatureFlags = GenericFeatureFlags & NamedFeatureFlags

export interface Preferences {
  currency?: string
  showVerifiedBanner?: boolean
  showCSATSurvey?: CSATSurveyData
  companyDescription?: string
}

export interface ProfileCountry {
  name: string
}
export interface Profile {
  id?: string | number
  key?: string
  firstName?: string
  lastName?: string
  emailAddress?: string
  roles?: string[]
  twoFactorEnabled?: boolean
  country?: ProfileCountry
  language?: string
  currencyCode?: string
  loggedIn?: boolean
  viewOnly?: boolean
  roleGroups?: string[]
}

export const EnvironmentTypes = {
  PRODUCTION: 'PRODUCTION',
  SANDBOX: 'SANDBOX',
  STAGING: 'STAGING',
  TEST: 'TEST',
} as const

export type EnvironmentTypes = (typeof EnvironmentTypes)[keyof typeof EnvironmentTypes]

export type URLType = string
export const Currencies = {
  EUR: 'EUR',
  GBP: 'GBP',
  ZAR: 'ZAR',
  NGN: 'NGN',
  USD: 'USD',
  AED: 'AED',
} as const
export type Currencies = (typeof Currencies)[keyof typeof Currencies]

export const CountryCodes = {
  NG: 'NG',
  ZA: 'ZA',
  US: 'US',
  UK: 'GB',
}

export type BankAccount = {
  accountDocument?: unknown
  currency: string
  description: string
  id: number
  instantFundingOptions: unknown[]
  options: BankAccountOptions
  otp?: unknown
  type: unknown
  reference?: string
  status?: string
  alias?: string
  beneficiaryPaymentReference?: string
  transferDestination?: BeneficiaryTransferDestination
  entityType?: BeneficiaryEntityType
}

export type BankAccountOptions = {
  routingNumber: string
  bankId: string
  countryCode: string
  accountType: string
  bankName: string
  countryName: string
  accountNumber: string
  accountHolderName: string
  iban: string
  bic: string
  description: string
  ukSortCode: string
  ukAccountNumber: string
  beneficiaryBank?: {
    address?: Address
    bic?: string
    name?: string
  }
}

export type Limits = {
  currency: string
  maximum: number
  minimum: number
  reason?: string
}

export type WithdrawalLimits = {
  current: Limits
  daily: Limits
  weekly: Limits
}

export type CurrencyLimits = {
  accountLimits: Limits
  currency: string
  depositLimits: Limits
  minimum: number
  maximum: number
  remaining: number
  withdrawalLimits?: WithdrawalLimits
}

export type CurrencyFilter = 'fiat-only' | 'crypto-only'

export type EnableEPWalletCurrencies = {
  supported?: CurrencyFilter
}

type EnableBanner = {
  enabled: boolean
  state: 'critical'
  message: string
  buttonText: string
  buttonLink: string
  variant: 'info' | 'warning' | 'success' | 'error'
}

export type ExchangeRate = {
  pair: string
  baseCode: string
  counterCode: string
  rate: number
}

export interface AuthToken {
  access_token?: string
  username?: string
  secret?: string
  impersonate?: boolean
  roles?: string[]
  length?: number
  token_type?: 'Hawk'
}

export const AccountTypes = {
  BUSINESS: 'business',
  DEVELOPER: 'developer',
} as const

export type AccountTypes = (typeof AccountTypes)[keyof typeof AccountTypes]

export const WalletTypes = {
  FIAT: 'FIAT',
  CRYPTO: 'CRYPTO',
  CUSTOMER: 'CUSTOMER',
}

export type WalletTypes = (typeof WalletTypes)[keyof typeof WalletTypes]

export interface RequestedDocument {
  id: number
  name: string
}

export const paymentDirections = {
  IN: 'IN',
  OUT: 'OUT',
}

export type ErrorResponse = {
  status: number
  data: {
    code: string
    message: string
  }
}

export const LogoColor = {
  DARK: 'black',
  LIGHT: 'white',
  INHERIT: 'currentColor',
} as const

export type LogoColorType = (typeof LogoColor)[keyof typeof LogoColor]

export type ExchangeRateResponse = {
  value: number
}

export interface CurrencyPreference {
  id?: number
  name: string
  value: string
}

export type AccountPreferenceKey = keyof Preferences

// Paginated responses
export interface Pageable {
  sort: Sort
  pageNumber: number
  pageSize: number
  offset: number
  paged: boolean
  unpaged: boolean
}

export interface Sort {
  sorted: boolean
  unsorted: boolean
  empty: boolean
}

export const ActionIds = {
  // Wallet actions
  MOVE: 'MOVE',
  PAY: 'PAY',
  WITHDRAW: 'WITHDRAW',
  FIAT_DEPOSIT: 'FIAT_DEPOSIT',
  SEND: 'SEND',
  CONVERT: 'CONVERT',
  FIAT_SEND: 'FIAT_SEND',
  // Payments
  PAYMENT_LINK: 'PAYMENT_LINK',
  CHANNEL_PAYMENT: 'CHANNEL_PAYMENT',
  // Channels
  CHANNEL_EDIT: 'CHANNEL_EDIT',
  // Contacts
  CONTACT_ASSIGN: 'CONTACT_ASSIGN',
  // Integrations
  API_KEY_CREATE: 'API_KEY_CREATE',
  API_KEY_MANAGE: 'API_KEY_MANAGE',
  // Mass payouts
  MASSPAYOUTS: 'MASSPAYOUTS',
  MASSPAYOUTS_UPLOAD: 'MASSPAYOUTS_UPLOAD',
  // Wallets
  WALLET_CREATE: 'WALLET_CREATE',
  WALLET_EDIT: 'WALLET_EDIT',
  // Account actions
  CHANGE_PASSWORD: 'CHANGE_PASSWORD',
  // Merchant actions
  MERCHANT_CREATE: 'MERCHANT_CREATE',
  MERCHANT_EDIT: 'MERCHANT_EDIT',
  MERCHANT_DETAILS: 'MERCHANT_DETAILS',
  // BVNK network
  NETWORK_DISCOVER_MEMBERS: 'NETWORK_DISCOVER_MEMBERS',
  NETWORK_EDIT_COMPANY: 'NETWORK_EDIT_COMPANY',
} as const

export type ActionIds = (typeof ActionIds)[keyof typeof ActionIds]

export interface SheetSubHeaderProps {
  subTitle: string
  stepper?: string
}

export interface Meta {
  sourceWallet?: WalletType
  customerWallet?: CustomerWallet
  handleContactSelect?: (contact: Contact) => void
  enableCreateInstrument?: boolean
  onPaymentChannelSuccess?: () => void
  onPaymentLinkSuccess?: () => void
  contactFilter?: string[]
  contactInstrument?: ContactInstrument
  flowType?: InstrumentType
  // Move flow
  sourceWalletType?: WalletTransferType
  sourceCustomerWallet?: CustomerWallet
  isEmbeddedCustomerModel?: boolean
  isRelianceCustomerModel?: boolean
  customerModel?: CustomerModel
  onPayoutSuccess?: () => void
  handleContactInstrumentUpdate?: (contactInstrument: ContactInstrument, contact?: Contact) => void
  // Fiat send flow
  fiatBeneficiary?: BeneficiaryDetails
  // Fiat deposit flow
  customerWalletDepositDetails?: CustomerWalletDepositDetails
  // Channel flow
  sourceChannel?: Channel
  onChannelEditSuccess?: () => void
  // Merchant flow
  merchant?: MerchantType
  // Convert flow
  convertFromCustomerWallet?: ConvertWallet
  // Bvnk network flow
  companyDetails?: unknown
}

export interface CommonSheet {
  header?: string
  subHeader?: SheetSubHeaderProps
  onClose: () => void
  onBack?: () => void | undefined
}

export interface ActionSheet extends CommonSheet {
  actionId?: ActionIds
  sourceWalletReference?: string
  sourceWalletId?: number
  meta?: Meta
}

export interface ErrorDetails {
  [key: string]: string[]
}

export const SheetName = {
  CONTACT: 'contact',
  INSTRUMENT: 'instrument',
  SEND: 'send',
} as const

export type SheetName = (typeof SheetName)[keyof typeof SheetName]

export const SheetState = {
  OPEN: 'open',
  CLOSED: 'closed',
} as const

export type SheetState = (typeof SheetState)[keyof typeof SheetState]

export type Sheet = Partial<{
  [K in SheetName]: SheetState
}>

export interface CountryState {
  code: string
  name: string
}

export type SortOrder = 'asc' | 'desc'

export interface ConvertWallet {
  id: string | number
  description: string
  lsid?: string
  balance: number
  currency: {
    code: string
    pricePrecision: number | undefined
  }
}
